/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { redirect } from "react-router-dom";
import DashboardWrapper from "../Layout/DashboardWrapper";
import ModalUI from "../legacy/Modal";
import TabsIndex from "./TabsIndex";
import {
  createDrug,
  updateDrug,
  deleteDrug,
  createDrugStock,
  getDrugs,
  getDrugsStock,
} from "../../services/drugs";
import { getUserData } from "../../utils/storageUtils";

const PAGE_SIZE = 20;
const DROPDOWN_PAGE_SIZE = 500;

const initialForm = (userId) => ({
  name: "",
  stock: 0,
  basePrice: 0,
  quantity: 0,
  userId,
  action: "create",
});

const Pharmacy = () => {
  const userData = getUserData();
  const queryClient = useQueryClient();

  const [form, setForm] = useState(initialForm(userData?.user?.id));

  // Pagination & recherche — état séparé pour le catalogue (TabOne) et le
  // suivi de ventes (TabTwo). Debounce 300 ms aligné sur Patient/Examen.
  const [drugSearch, setDrugSearch] = useState("");
  const [debouncedDrugSearch, setDebouncedDrugSearch] = useState("");
  const [drugPage, setDrugPage] = useState(0);

  const [stockSearch, setStockSearch] = useState("");
  const [debouncedStockSearch, setDebouncedStockSearch] = useState("");
  const [stockPage, setStockPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedDrugSearch(drugSearch);
      setDrugPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [drugSearch]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedStockSearch(stockSearch);
      setStockPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [stockSearch]);

  // Liste paginée — catalogue médicaments (TabOne).
  const {
    data: drugsResult,
    isLoading: drugsLoading,
    isFetching: drugsFetching,
  } = useQuery(
    ["drugs", debouncedDrugSearch, drugPage],
    () => getDrugs({ search: debouncedDrugSearch, page: drugPage, size: PAGE_SIZE }),
    { staleTime: 30000, keepPreviousData: true }
  );

  // Liste paginée — mouvements de stock (TabTwo).
  const {
    data: stockResult,
    isLoading: stockLoading,
    isFetching: stockFetching,
  } = useQuery(
    ["drugs-stock", debouncedStockSearch, stockPage],
    () => getDrugsStock({ search: debouncedStockSearch, page: stockPage, size: PAGE_SIZE }),
    { staleTime: 30000, keepPreviousData: true }
  );

  // Liste plate "tous les médicaments" pour le <select> de TabTwo — pattern
  // dropdown identique à Patient (modale create) : on charge une grosse
  // page une seule fois plutôt qu'un Combobox search-as-you-type. Si une
  // clinique dépasse cette volumétrie, basculer sur `getDrugsLookup`.
  const { data: drugsAllResult } = useQuery(
    ["drugs-all"],
    () => getDrugs({ search: "", page: 0, size: DROPDOWN_PAGE_SIZE }),
    { staleTime: 60000 }
  );

  const paginatedDrugs = drugsResult?.data || {};
  const drugs = paginatedDrugs.content || [];
  const drugsPagination = {
    totalElements: paginatedDrugs.totalElements || 0,
    totalPages: paginatedDrugs.totalPages || 0,
    currentPage: paginatedDrugs.currentPage || 0,
    hasNext: paginatedDrugs.hasNext || false,
    hasPrevious: paginatedDrugs.hasPrevious || false,
  };

  const paginatedStock = stockResult?.data || {};
  const drugsSuivie = paginatedStock.content || [];
  const stockPagination = {
    totalElements: paginatedStock.totalElements || 0,
    totalPages: paginatedStock.totalPages || 0,
    currentPage: paginatedStock.currentPage || 0,
    hasNext: paginatedStock.hasNext || false,
    hasPrevious: paginatedStock.hasPrevious || false,
  };

  const drugsAll = drugsAllResult?.data?.content || [];

  // Mutations — `apiRequest.notifyApiError` toaste déjà les `!ok`, on ne
  // refait pas `toast.error(result.message)` ici. On garde le toast.success
  // et l'invalidation de cache. Cf. Examen pour le même pattern.
  const { mutate: mutateCreateDrug, isLoading: isCreating } = useMutation(
    (payload) => createDrug(payload),
    {
      onSuccess: (result) => {
        if (!result?.ok) return;
        toast.success("Médicament créé avec succès");
        resetForm();
        queryClient.invalidateQueries(["drugs"]);
        queryClient.invalidateQueries(["drugs-all"]);
      },
    }
  );

  const { mutate: mutateUpdateDrug, isLoading: isUpdating } = useMutation(
    (payload) => updateDrug(payload),
    {
      onSuccess: (result) => {
        if (!result?.ok) return;
        toast.success("Médicament mis à jour");
        resetForm();
        queryClient.invalidateQueries(["drugs"]);
        queryClient.invalidateQueries(["drugs-all"]);
      },
    }
  );

  const { mutate: mutateDeleteDrug, isLoading: isDeleting } = useMutation(
    (payload) => deleteDrug(payload),
    {
      onSuccess: (result) => {
        if (!result?.ok) return;
        toast.success("Médicament supprimé");
        setDeleteCandidate(null);
        queryClient.invalidateQueries(["drugs"]);
        queryClient.invalidateQueries(["drugs-all"]);
      },
    }
  );

  const { mutate: mutateCreateStock, isLoading: isCreatingStock } = useMutation(
    (payload) => createDrugStock(payload),
    {
      onSuccess: (result) => {
        if (!result?.ok) return;
        toast.success("Mouvement de stock enregistré");
        queryClient.invalidateQueries(["drugs-stock"]);
        queryClient.invalidateQueries(["drugs"]);
        // Re-init la quantité saisie (mais on garde le médicament sélectionné).
        setForm((f) => ({ ...f, quantity: 0 }));
      },
    }
  );

  const resetForm = () => setForm(initialForm(userData?.user?.id));

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.action === "create") {
      mutateCreateDrug(form);
    } else if (form.action === "edit") {
      mutateUpdateDrug(form);
    }
  };

  const handleStockSubmit = (e) => {
    e.preventDefault();
    mutateCreateStock(form);
  };

  const handleUpdate = (e, drug) => {
    e.preventDefault();
    if (!drug) {
      // Bouton « Effacer » de TabOne — pas de drug, juste reset.
      resetForm();
      return;
    }
    setForm({ ...drug, action: "edit" });
  };

  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const handleDelete = (e, drug) => {
    e.preventDefault();
    setDeleteCandidate(drug);
  };

  useEffect(() => {
    if (!userData) {
      redirect("/login");
    }
  }, [userData]);

  return (
    <DashboardWrapper
      showSpinner={
        drugsLoading ||
        drugsFetching ||
        stockLoading ||
        stockFetching ||
        isCreating ||
        isUpdating ||
        isDeleting ||
        isCreatingStock
      }
    >
      {/* Confirmation suppression — pattern aligné sur Patient/Assureur. */}
      <ModalUI
        isOpen={deleteCandidate != null}
        onClose={() => {
          if (!isDeleting) setDeleteCandidate(null);
        }}
        styles={{ width: "min(440px, 92vw)", padding: 0 }}
      >
        <div className="flex flex-col">
          <div className="flex items-start gap-4 px-6 pt-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-100">
              <i className="bx bx-trash text-2xl" />
            </div>
            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-[0.32em] text-rose-600 font-semibold">
                Action irréversible
              </p>
              <h2 className="text-lg font-semibold text-slate-900 leading-tight">
                Supprimer ce médicament ?
              </h2>
              <p className="text-sm text-slate-500">
                Le médicament{" "}
                <span className="font-medium text-slate-800">
                  {deleteCandidate?.name || "—"}
                </span>{" "}
                sera supprimé définitivement du catalogue. Cette action ne
                peut pas être annulée.
              </p>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
            <button
              type="button"
              onClick={() => setDeleteCandidate(null)}
              disabled={isDeleting}
              className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => {
                if (deleteCandidate?.id != null) {
                  mutateDeleteDrug(deleteCandidate);
                }
              }}
              disabled={isDeleting || deleteCandidate?.id == null}
              className="flex-[1.4] inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-sm font-medium shadow-lg shadow-rose-600/25 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <>
                  <i className="bx bx-loader-alt animate-spin text-base" />
                  <span>Suppression…</span>
                </>
              ) : (
                <>
                  <i className="bx bx-trash text-base" />
                  <span>Supprimer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </ModalUI>
      <div className="space-y-8">
        <TabsIndex
          form={form}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          handleUpdate={handleUpdate}
          handleDelete={handleDelete}
          handleStockSubmit={handleStockSubmit}
          drugs={drugs}
          drugsAll={drugsAll}
          drugsSuivie={drugsSuivie}
          drugSearch={drugSearch}
          setDrugSearch={setDrugSearch}
          drugsPagination={drugsPagination}
          onDrugPageChange={setDrugPage}
          stockSearch={stockSearch}
          setStockSearch={setStockSearch}
          stockPagination={stockPagination}
          onStockPageChange={setStockPage}
          drugsFetching={drugsFetching}
          stockFetching={stockFetching}
        />
      </div>
    </DashboardWrapper>
  );
};

export default Pharmacy;
